import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { Bell, Package, Mail, CheckCircle } from 'lucide-react';

const RestockRequestsManager = () => {
  const [requests, setRequests] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending');
  const [confirmModal, setConfirmModal] = useState({ show: false, productId: null, productTitle: '' });
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/shop/restock-requests?status=${statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests);
        setMetrics(data.metrics);
      }
    } catch (error) {
      console.error('Error fetching restock requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch restock requests',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyCustomers = async (productId, productTitle) => {
    // Show custom modal instead of browser confirm
    setConfirmModal({ show: true, productId, productTitle });
  };

  const confirmNotifyCustomers = async () => {
    const { productId, productTitle } = confirmModal;
    setConfirmModal({ show: false, productId: null, productTitle: '' });
    
    setNotifying({ ...notifying, [productId]: true });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/shop/restock-requests/notify-product/${productId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();

      if (res.ok) {
        toast({
          title: 'Success!',
          description: data.message || `Notified ${data.notified_count} customers`,
        });
        fetchRequests(); // Refresh
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to send notifications',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong',
        variant: 'destructive'
      });
    } finally {
      setNotifying({ ...notifying, [productId]: false });
    }
  };

  return (
    <div className="space-y-6">
      {/* Modern Header with gradient */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900 rounded-lg p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-white dark:bg-gray-800/20 p-3 rounded-lg">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Restock Requests</h2>
        </div>
        <p className="text-blue-100 ml-14">
          Customer demand tracking for out-of-stock products
        </p>
      </div>

      {/* Modern Status Filter Pills */}
      <div className="flex gap-2 bg-gray-100 dark:bg-gray-800 p-2 rounded-lg inline-flex">
        <button
          className={`px-4 py-2 rounded-md font-medium transition-all ${
            statusFilter === 'pending'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700'
          }`}
          onClick={() => setStatusFilter('pending')}
        >
          🔴 Pending {metrics.filter(m => m.pending_requests > 0).length > 0 && `(${metrics.filter(m => m.pending_requests > 0).length})`}
        </button>
        <button
          className={`px-4 py-2 rounded-md font-medium transition-all ${
            statusFilter === 'notified'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700'
          }`}
          onClick={() => setStatusFilter('notified')}
        >
          ✅ Notified
        </button>
        <button
          className={`px-4 py-2 rounded-md font-medium transition-all ${
            statusFilter === 'all'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700'
          }`}
          onClick={() => setStatusFilter('all')}
        >
          📊 All Requests
        </button>
      </div>

      {/* Modern Product Cards with gradient borders */}
      {metrics.length > 0 && statusFilter === 'pending' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.filter(m => m.pending_requests > 0).map((metric) => (
            <Card key={metric.product_id} className="dark:bg-gray-800 dark:border-gray-700 hover:shadow-xl transition-shadow border-t-4 border-t-red-500">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base dark:text-gray-100 line-clamp-2 font-semibold">
                      {metric.product_title}
                    </CardTitle>
                  </div>
                  <Badge className="bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1 text-sm font-bold">
                    {metric.pending_requests} 🔥
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="w-4 h-4" />
                    <span>{metric.user_emails.length} customers waiting</span>
                  </div>
                  
                  {/* Customer Emails */}
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {metric.user_emails.map((user, idx) => (
                      <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        <span className="truncate">{user.name} ({user.email})</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold shadow-md"
                    size="sm"
                    onClick={() => handleNotifyCustomers(metric.product_id, metric.product_title)}
                    disabled={notifying[metric.product_id]}
                  >
                    {notifying[metric.product_id] ? (
                      <>
                        <span className="animate-pulse">Sending...</span>
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4 mr-2" />
                        Notify All Customers
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && metrics.filter(m => m.pending_requests > 0).length === 0 && statusFilter === 'pending' && (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="py-12">
            <div className="text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No Pending Requests
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                All restock requests have been handled
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Requests Table */}
      {statusFilter !== 'pending' && (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-gray-100">All Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</p>
            ) : requests.length === 0 ? (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">No requests found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Product</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Customer</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Requested</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-gray-100">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} className="border-b dark:border-gray-700">
                        <td className="py-3 px-4 dark:text-gray-300">{request.product_title}</td>
                        <td className="py-3 px-4 dark:text-gray-300">
                          {request.user_name}<br />
                          <span className="text-sm text-gray-500 dark:text-gray-400">{request.user_email}</span>
                        </td>
                        <td className="py-3 px-4 dark:text-gray-300">
                          {new Date(request.requested_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={request.status === 'notified' ? 'bg-green-500' : 'bg-yellow-500'}>
                            {request.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modern Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-in fade-in zoom-in duration-200">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="bg-white dark:bg-gray-800/20 p-3 rounded-full">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Send Notifications</h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed mb-4">
                You are about to send restock notifications to all customers who requested:
              </p>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border dark:border-gray-700-blue-500 p-4 rounded-r-lg mb-6">
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  {confirmModal.productTitle}
                </p>
              </div>

              <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p>Customers will receive a professional email with a "Shop Now" button linking directly to this product.</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, productId: null, productTitle: '' })}
                  className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmNotifyCustomers}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4" />
                    Send Emails
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestockRequestsManager;
